<template>
  <div class="faq-page">
    <Navbar :currentLang="currentLang" @change-lang="changeLang" />
    
    <section class="faq-hero">
      <div class="container">
        <div class="hero-content">
          <h1>{{ t.faq.title }}</h1>
          <p class="hero-subtitle">{{ t.faq.description }}</p>
        </div>
        <div class="breadcrumb">
          <a href="/">{{ t.nav.home }}</a>
          <span>/</span>
          <span>{{ t.faq.title }}</span>
        </div>
      </div>
    </section>
    
    <section class="faq-section">
      <div class="container">
        <div class="faq-sidebar">
          <nav class="category-nav">
            <button 
              v-for="(category, index) in faqCategories" 
              :key="index"
              @click="activeCategory = index"
              :class="{ active: activeCategory === index }"
              class="category-item"
            >
              <span class="category-icon">{{ index + 1 }}</span>
              <span class="category-name">{{ category }}</span>
            </button>
          </nav>
        </div>
        
        <div class="faq-main">
          <div class="section-header">
            <h2>{{ faqCategories[activeCategory] }}</h2>
            <span class="question-count">{{ filteredFAQs.length }} {{ t.faq.questions }}</span>
          </div>
          
          <div class="faq-accordion">
            <div 
              v-for="(item, index) in filteredFAQs" 
              :key="index"
              class="accordion-item"
            >
              <button 
                class="accordion-header"
                :class="{ active: openFAQ === index }"
                @click="toggleFAQ(index)"
              >
                <span class="accordion-number">{{ index + 1 }}</span>
                <span class="accordion-question">{{ item.question }}</span>
                <span class="accordion-icon">
                  <svg v-if="openFAQ !== index" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 5v14M5 12h14"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12h14"/>
                  </svg>
                </span>
              </button>
              <div 
                class="accordion-content"
                :class="{ open: openFAQ === index }"
              >
                <p>{{ item.answer }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    
    <section class="cta-section">
      <div class="container">
        <div class="cta-content">
          <h3>{{ t.faq.contactTitle }}</h3>
          <p>{{ t.faq.contactText }}</p>
          <button class="cta-button" @click="scrollToContact">{{ t.faq.contactBtn }}</button>
        </div>
      </div>
    </section>
    
    <Footer :currentLang="currentLang" />
  </div>
</template>

<script>
import Navbar from '../components/Navbar.vue'
import Footer from '../components/Footer.vue'
import { getSafeTranslator } from '../assets/languages.js'

export default {
  name: 'FAQ',
  props: {
    currentLang: {
      type: String,
      default: 'en'
    }
  },
  components: {
    Navbar,
    Footer
  },
  data() {
    return {
      openFAQ: null,
      activeCategory: 0
    }
  },
  computed: {
    t() {
      return getSafeTranslator(this.currentLang)
    },
    allFAQs() {
      return this.t.faq.items || []
    },
    faqCategories() {
      return [...new Set(this.allFAQs.map(item => item.category))]
    },
    filteredFAQs() {
      if (!this.allFAQs.length || !this.faqCategories.length) return []
      const category = this.faqCategories[this.activeCategory]
      return this.allFAQs.filter(item => item.category === category)
    }
  },
  watch: {
    currentLang() {
      this.openFAQ = null
      this.activeCategory = 0
    },
    activeCategory() {
      this.openFAQ = null
    }
  },
  methods: {
    changeLang(lang) {
      this.$emit('change-lang', lang)
    },
    toggleFAQ(index) {
      this.openFAQ = this.openFAQ === index ? null : index
    },
    scrollToContact() {
      this.$router.push('/contact')
    }
  }
}
</script>

<style scoped>
.faq-page {
  min-height: 100vh;
  background-color: #ffffff;
}

.faq-hero {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 6rem 0 4rem;
  color: white;
}

.faq-hero .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.hero-content {
  text-align: center;
  margin-bottom: 2rem;
}

.hero-content h1 {
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #94a3b8;
  max-width: 600px;
  margin: 0 auto;
}

.breadcrumb {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #94a3b8;
  font-size: 0.95rem;
}

.breadcrumb a {
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb a:hover {
  color: white;
}

.faq-section {
  padding: 5rem 0;
  background-color: #f8fafc;
}

.faq-section .container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  gap: 4rem;
}

.faq-sidebar {
  width: 280px;
  flex-shrink: 0;
  position: sticky;
  top: 4rem;
  height: fit-content;
}

.category-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.category-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background-color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.category-item:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.category-item.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.category-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
}

.category-item.active .category-icon {
  background-color: rgba(255, 255, 255, 0.2);
}

.category-name {
  font-weight: 500;
  font-size: 0.95rem;
}

.faq-main {
  flex: 1;
  min-width: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
}

.question-count {
  padding: 0.375rem 0.875rem;
  background-color: #e0f2fe;
  color: #0369a1;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.faq-accordion {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.accordion-item {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.3s ease;
}

.accordion-header:hover {
  background-color: #f8fafc;
}

.accordion-header.active {
  background-color: #eff6ff;
}

.accordion-number {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f1f5f9;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.accordion-header.active .accordion-number {
  background-color: #3b82f6;
  color: white;
}

.accordion-question {
  flex: 1;
  font-weight: 600;
  font-size: 1rem;
  color: #1e293b;
  line-height: 1.5;
}

.accordion-header.active .accordion-question {
  color: #1d4ed8;
}

.accordion-icon {
  width: 20px;
  height: 20px;
  color: #94a3b8;
  transition: color 0.3s ease;
  flex-shrink: 0;
}

.accordion-header.active .accordion-icon {
  color: #3b82f6;
}

.accordion-icon svg {
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.4s ease;
}

.accordion-content.open {
  max-height: 500px;
}

.accordion-content p {
  padding: 0 1.5rem 1.5rem;
  color: #64748b;
  line-height: 1.8;
  font-size: 0.95rem;
}

.cta-section {
  padding: 5rem 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

.cta-section .container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
}

.cta-content h3 {
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
}

.cta-content p {
  color: #94a3b8;
  font-size: 1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.cta-button {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

@media (max-width: 1024px) {
  .faq-section .container {
    flex-direction: column;
    gap: 2rem;
  }
  
  .faq-sidebar {
    width: 100%;
    position: static;
  }
  
  .category-nav {
    flex-direction: row;
    flex-wrap: wrap;
  }
  
  .category-item {
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .faq-hero {
    padding: 4rem 0 3rem;
  }
  
  .hero-content h1 {
    font-size: 2.5rem;
  }
  
  .hero-subtitle {
    font-size: 1.1rem;
  }
  
  .faq-section {
    padding: 3rem 0;
  }
  
  .section-header h2 {
    font-size: 1.5rem;
  }
  
  .accordion-header {
    padding: 1rem 1.25rem;
  }
  
  .accordion-question {
    font-size: 0.9rem;
  }
  
  .accordion-content p {
    padding: 0 1.25rem 1.25rem;
    font-size: 0.9rem;
  }
  
  .cta-section {
    padding: 4rem 0;
  }
  
  .cta-content h3 {
    font-size: 1.5rem;
  }
}

@media (max-width: 480px) {
  .hero-content h1 {
    font-size: 2rem;
  }
  
  .category-item {
    padding: 0.875rem 1rem;
    font-size: 0.875rem;
  }
  
  .category-icon {
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }
}
</style>